import {
  type UseMutationResult,
  type UseQueryResult,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import { adminMutationKeys, adminQueryKeys } from '@/hooks/admin/query-keys';
import { retryControlApi } from '@/hooks/admin/retry';
import { featureFlagsService } from '@/services/control/feature-flags-service';
import {
  isEnabledForTenant,
  type FeatureFlag,
} from '@/types/control/feature-flag';
import { ReadModelUnavailableError, pollUntilCondition } from '@/utils/poll';

export function useFeatureFlags(): UseQueryResult<FeatureFlag[], Error> {
  return useQuery<FeatureFlag[], Error>({
    queryKey: adminQueryKeys.featureFlags(),
    queryFn: ({ signal }) => featureFlagsService.list(signal),
    retry: retryControlApi,
  });
}

export type SetFlagArgs = { flagName: string; enabled: boolean };

/**
 * Toggle a flag for one tenant, then wait until a read agrees.
 *
 * The wait is not optional politeness. mesh caches flag state per process for
 * five seconds and a write refreshes only the writing process's copy, so
 * control's proxy — which lands on whichever mesh replica the load balancer
 * picks — can answer the confirming read with the pre-toggle value. Without
 * this the switch visibly flips back.
 */
export function useSetTenantFeatureFlag(
  tenantId: string
): UseMutationResult<void, Error, SetFlagArgs> {
  const queryClient = useQueryClient();

  return useMutation<void, Error, SetFlagArgs>({
    mutationKey: adminMutationKeys.setTenantFeatureFlag(tenantId),
    mutationFn: async ({ flagName, enabled }) => {
      await featureFlagsService.setForTenant(tenantId, flagName, enabled);

      try {
        await pollUntilCondition(
          async () => {
            const flags = await featureFlagsService.list();
            const flag = flags.find((f) => f.flagName === flagName);
            return flag
              ? isEnabledForTenant(flag, tenantId) === enabled
              : false;
          },
          { maxAttempts: 12, delayMs: 700 }
        );
      } catch (err) {
        // A read model that keeps erroring is a real failure and must surface.
        if (err instanceof ReadModelUnavailableError) {
          throw err;
        }
        // Running out of attempts is not. The write already succeeded — the
        // caches are simply still catching up — and reporting failure here is
        // the HYD-67 bug: the user retries and toggles it back.
      }
    },
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: adminQueryKeys.featureFlags(),
      }),
  });
}
